import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "How can I create a Blockfirst Capital account?",
      answer: "Creating a Blockfirst Capital account is simple and secure. Download our mobile app or visit our website, provide your basic information, complete identity verification, and you'll be ready to start investing in minutes."
    },
    {
      question: "How do I start earning interest?",
      answer: "Once your account is verified, you can deposit cryptocurrencies or fiat currency. Choose from our range of interest-earning products, and your assets will start generating returns immediately with competitive rates up to 16% annually."
    },
    {
      question: "Is my money safe with Blockfirst Capital?",
      answer: "Yes, your funds are protected by industry-leading security measures including 256-bit encryption, cold storage solutions, and comprehensive insurance coverage. We maintain full regulatory compliance across all jurisdictions."
    },
    {
      question: "What cryptocurrencies do you support?",
      answer: "We support over 50+ major cryptocurrencies including Bitcoin, Ethereum, and many others. Our platform regularly adds new assets based on market demand and regulatory compliance."
    },
    {
      question: "How quickly can I access my funds?",
      answer: "Most withdrawals are processed within minutes to hours, depending on the cryptocurrency and network conditions. Fiat withdrawals typically complete within 1-3 business days."
    },
    {
      question: "Do you offer customer support?",
      answer: "Yes, our dedicated team provides 24/7 customer support through live chat, email, and phone. We're committed to helping you with any questions or concerns you may have."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 1.2, 
            ease: [0.25, 0.46, 0.45, 0.94],
            delay: 0.2 
          }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-12 lg:mb-16"
        >
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-6"
          >
            Frequently asked questions.
          </motion.h2>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.8, 
                delay: index * 0.1 
              }}
              viewport={{ once: true }}
              className="mb-4"
            >
              <motion.div
                className="border border-gray-200 rounded-xl overflow-hidden"
                whileHover={{ 
                  borderColor: "#e5e7eb",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
                }}
              >
                <motion.button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 lg:px-8 lg:py-6 text-left flex items-center justify-between bg-white hover:bg-gray-50 transition-colors duration-200"
                  whileHover={{ backgroundColor: "#f9fafb" }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="text-lg lg:text-xl font-semibold text-gray-900">
                    {faq.question}
                  </span>
                  <motion.div
                    animate={{ rotate: openIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex-shrink-0 ml-4"
                  >
                    {openIndex === index ? (
                      <MinusIcon className="w-6 h-6 text-gray-600" />
                    ) : (
                      <PlusIcon className="w-6 h-6 text-gray-600" />
                    )}
                  </motion.div>
                </motion.button>

                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 py-4 lg:px-8 lg:py-6 bg-gray-50 border-t border-gray-200">
                        <p className="text-gray-700 text-base lg:text-lg leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection; 